import React, { Fragment, useMemo } from 'react';
import { Typography } from '@vp/swan';
import { ValidationState, ValidatorType } from '@vp/upload-and-print-validator';
import { usePerfectBound } from 'src/hooks/usePerfectBound';
import { useTrackingEvent } from 'src/hooks/useTrackingEvent';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { text } from 'src/utils/localizationUtil';
import { ValidationErrorDataType, validationErrorsMessages } from 'src/utils/validationErrorsMessage';
import { getUnEmbeddedFilePages } from 'src/utils/validationUtil';
import { ValidationCard } from './ValidationCard';
import { AnalyticsEventData } from '../Analytics';

interface Props {
  bodyDescription?: string;
  showCardTitle?: boolean;
}

export const ValidationErrorCardContainer = ({ bodyDescription, showCardTitle }: Props) => {
  const { validationPlaceholders, validations } = useValidationContext();
  const { isPerfectBound, getPerfectBoundPageCountTokenValues } = usePerfectBound();

  const { sendSegmentTrackingEvent } = useTrackingEvent();

  const corruptValidator = validations?.find(validation => validation.id === ValidatorType.CorruptValidator);
  const fileValidationMessage = corruptValidator?.message ?? '';
  const unEmbeddedPages = corruptValidator?.details?.pagesData;

  const validationsWithErrorState = (validations ?? validationPlaceholders ?? []).filter(
    validation => validation.state === ValidationState.Error
  );

  validationsWithErrorState.forEach((item: ValidationErrorDataType) => {
    const additionalData = validationErrorsMessages[item.id as keyof typeof validationErrorsMessages];
    if (additionalData) {
      const hasNonEmbeddedFontsError =
        additionalData.id === ValidatorType.CorruptValidator && unEmbeddedPages.length > 0;
      item.modalTitle = hasNonEmbeddedFontsError
        ? text('validations.non embedded fonts.error-modal-title')
        : additionalData.modalTitle;
      item.errorDescription = hasNonEmbeddedFontsError
        ? text(fileValidationMessage, { pageNumbers: getUnEmbeddedFilePages(unEmbeddedPages) })
        : additionalData.errorDescription;
    }
  });

  // Check if there are any validations with error state or computed validations
  const showDescription = useMemo(() => validationsWithErrorState.length > 0, [validationsWithErrorState.length]);

  const showTemplateMismatchValidation = useMemo(() => {
    const hasOnlyTemplateMismatch = validations?.every(
      validation =>
        (validation.id === ValidatorType.TemplateMismatchValidator ||
          validation.id === ValidatorType.PageSizeValidator) &&
        validation.state === ValidationState.Error
    );
    if (hasOnlyTemplateMismatch) {
      sendSegmentTrackingEvent({
        action: 'Template Mismatch Error',
        category: 'Design - Upload and Print',
        label: 'Template Mismatch Error',
      } as AnalyticsEventData);

      return true;
    }
    return false;
  }, [sendSegmentTrackingEvent, validations]);

  /**
   * Template mismatch is derived from page size validation for perfect bound products.
   * Hence, if there is a template mismatch validation, we show only that validation card.
   */
  if (showTemplateMismatchValidation) {
    return (
      <Fragment>
        <ValidationCard
          cardTitle={
            showCardTitle ? text(validationErrorsMessages['TemplateMismatchValidator'].modalTitle ?? '') : undefined
          }
          cardMessage={text(validationErrorsMessages['TemplateMismatchValidator'].errorDescription ?? '')}
          type="error"
        />
        {bodyDescription ? <Typography mt={3}>{bodyDescription}</Typography> : null}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {validationsWithErrorState.map((item: ValidationErrorDataType) => {
        const tokenValues = isPerfectBound ? getPerfectBoundPageCountTokenValues(item) : item.tokenValues;
        return (
          <ValidationCard
            key={item.id}
            cardTitle={showCardTitle ? text(item.modalTitle || '', item.tokenValues) : undefined}
            cardMessage={text(item.errorDescription || '', tokenValues)}
            type="error"
          />
        );
      })}
      {showDescription && bodyDescription ? <Typography mt={3}>{bodyDescription}</Typography> : null}
    </Fragment>
  );
};
